import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Spinner } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getProtocols } from '../../clients/protocol_client';
import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import ProtocolModal from '../../components/Protocol/ProtocolModal';
import ProtocolTable from '../../components/Protocol/ProtocolTable';
import { getProtocolErrorMessage } from '../../utils/protocols';
import { showToast } from '../../config_and_helpers/helpers';
import {
  logListSearchError,
  logListSearchEvent,
} from '../../config_and_helpers/logger';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';

const LIST_ENTITY = 'protocols';

/**
 * Context sharing the protocol list and its setter with child components.
 */
export const ProtocolContext = React.createContext();

/**
 * Protocols page: paginated, searchable list of the lab's SOP records with
 * their linked inventory products.
 *
 * `cat_num` and `product_id` are read from the URL so the product detail
 * section can deep-link into a pre-filtered list.
 *
 * @returns {JSX.Element}
 */
const ProtocolsPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [searchParams, setSearchParams] = useSearchParams();

  const catNumFilter = searchParams.get('cat_num') || '';
  const productIdFilter = searchParams.get('product_id') || '';

  const isMountedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const hasInitialFetchRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);
  const hasMoreRef = useRef(true);

  const [protocols, setProtocols] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  /**
   * Fetch protocols with the active filters.
   *
   * @param {Object} params
   * @param {string} [params.searchValue] - Text search term.
   * @param {string|null} [params.nextPage] - Backend next-page URL.
   * @param {string} [params.catNum] - Linked product catalogue number.
   * @param {string} [params.productId] - Linked product pk.
   * @param {boolean} [newSearch] - True when replacing the list rather than paginating.
   */
  const fetchProtocols = useCallback(
    (
      { searchValue = '', nextPage = null, catNum = '', productId = '' } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue,
        catNum,
        productId,
        nextPage,
      };

      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }

      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        setProtocols([]);
      }
      isLoadingRef.current = true;

      const controller = new AbortController();
      currentRequestRef.current = controller;

      getProtocols(token, setProtocols, {
        searchInput: searchValue,
        catNum,
        productId,
        nextPage,
        signal: controller.signal,
      })
        .then(response => {
          if (currentRequestRef.current !== controller) return;

          if (response?.canceled) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          if (response?.success) {
            setNextPageUrl(response.reachedEnd ? null : response.nextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            showToast(
              getProtocolErrorMessage(
                response,
                'An unexpected error occurred. Please try again'
              ),
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            LIST_ENTITY,
            response?.success ? 'success' : 'failure',
            {
              trigger,
              params,
              responseMeta: {
                success: response?.success,
                reachedEnd: !!response?.reachedEnd,
                nextPage: response?.nextPage,
              },
            }
          );

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) return;
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError(LIST_ENTITY, trigger, error, { params });

          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        });
    },
    [token]
  );

  // Initial fetch, and a refetch whenever the URL-driven product filters change.
  useEffect(() => {
    isMountedRef.current = true;
    hasInitialFetchRef.current = true;

    fetchProtocols(
      {
        searchValue: '',
        nextPage: null,
        catNum: catNumFilter,
        productId: productIdFilter,
      },
      true
    );

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchProtocols, catNumFilter, productIdFilter]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
    };
  }, []);

  const debouncedFetch = useCallback(
    value => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (value.length >= 2) {
        setProtocols([]);
        setIsSearching(true);
      }

      if (isRunnableListSearchQuery(value)) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchProtocols(
            {
              searchValue: value,
              nextPage: null,
              catNum: catNumFilter,
              productId: productIdFilter,
            },
            true
          );
        }, 300);
      } else if (isPartialListSearchQuery(value)) {
        setProtocols([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchProtocols, catNumFilter, productIdFilter]
  );

  const handleSearchInput = value => {
    setSearchInput(value);
    debouncedFetch(value);
  };

  const resetSearchValue = () => setSearchInput('');

  /** Drop the URL product filter; the effect above refetches the full list. */
  const clearProductFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('cat_num');
    next.delete('product_id');
    setSearchParams(next, { replace: true });
  };

  const refreshList = () =>
    fetchProtocols(
      {
        searchValue: searchInput,
        nextPage: null,
        catNum: catNumFilter,
        productId: productIdFilter,
      },
      true
    );

  const hasProductFilter = Boolean(catNumFilter || productIdFilter);

  return (
    <GlassWorkspaceShell theme="neutral" wide>
      <ProtocolContext.Provider value={{ protocols, setProtocols }}>
        <div className="glass-toolbar">
          <GenericSearchFilter
            entityType="protocols"
            placeHolderLabel="Protocol"
            searchInput={searchInput}
            onSearchInput={handleSearchInput}
            entity=""
            onEntityChange={() => {}}
            hideEntityDropdown
            // Protocols filter on search/cat_num/product_id only; an empty
            // options list keeps the shared toolbar from fetching suppliers.
            entityOptionsList={[]}
            ModalComponent={
              isViewer ? null : (
                <ProtocolModal
                  onSuccessfulSubmit={refreshList}
                  clearSearchValue={resetSearchValue}
                />
              )
            }
          />
        </div>

        {hasProductFilter && (
          <div className="d-flex align-items-center gap-2 mb-3">
            <Badge bg="secondary">
              {catNumFilter
                ? `Filtered by CAT# ${catNumFilter}`
                : `Filtered by product #${productIdFilter}`}
            </Badge>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={clearProductFilter}
            >
              Show all protocols
            </Button>
          </div>
        )}

        {(isLoading && protocols.length === 0) || isSearching ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
            {isSearching && searchInput.length >= 2 && (
              <div className="mt-2 text-muted">Searching protocols...</div>
            )}
          </div>
        ) : (
          <>
            {!isLoading && !isSearching && protocols.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-3">
                  <span
                    role="img"
                    aria-label="no results"
                    style={{ fontSize: '3rem' }}
                  >
                    🧪
                  </span>
                </div>
                <h5 className="text-muted mb-2">No protocols found</h5>
                <p className="text-muted mb-3">
                  {searchInput.length >= 2
                    ? `No protocols match your search for "${searchInput}"`
                    : hasProductFilter
                      ? 'No protocols are linked to this product yet'
                      : 'No protocols available'}
                </p>
                {searchInput.length >= 2 && (
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSearchInput('');
                      fetchProtocols(
                        {
                          searchValue: '',
                          nextPage: null,
                          catNum: catNumFilter,
                          productId: productIdFilter,
                        },
                        true
                      );
                    }}
                  >
                    <span role="img" aria-label="clear search">
                      🗑️
                    </span>{' '}
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <InfiniteScroll
                pageStart={0}
                loadMore={() => {
                  if (
                    isLoadingRef.current ||
                    !hasInitialFetchRef.current ||
                    !nextPageUrl ||
                    isPartialListSearchQuery(searchInput)
                  ) {
                    return;
                  }

                  const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                    search: searchInput,
                    cat_num: catNumFilter,
                    product_id: productIdFilter,
                  });
                  if (mismatch === null) {
                    logListSearchError(
                      LIST_ENTITY,
                      'next-page-url-stale-check',
                      new Error('Failed to parse nextPageUrl'),
                      { nextPageUrl, searchInput, catNumFilter, productIdFilter }
                    );
                    setHasMore(false);
                    hasMoreRef.current = false;
                    setNextPageUrl(null);
                    return;
                  }
                  if (mismatch.mismatched) {
                    logListSearchEvent(
                      LIST_ENTITY,
                      'next-page-url-stale-mismatch',
                      {
                        nextPageUrl,
                        expected: mismatch.expected,
                        actual: mismatch.actual,
                      }
                    );
                    setHasMore(false);
                    hasMoreRef.current = false;
                    setNextPageUrl(null);
                    return;
                  }

                  fetchProtocols(
                    {
                      searchValue: searchInput,
                      nextPage: nextPageUrl,
                      catNum: catNumFilter,
                      productId: productIdFilter,
                    },
                    false
                  );
                }}
                hasMore={hasMore}
                loader={
                  <div key="protocol-loader" className="text-center py-2">
                    <Spinner animation="border" size="sm" role="status" />
                    <div className="mt-1">
                      <small className="text-muted">
                        Loading more protocols...
                      </small>
                    </div>
                  </div>
                }
                useWindow={true}
                threshold={INFINITE_SCROLL_THRESHOLD_PX}
              >
                <div className="glass-table-wrap">
                  <ProtocolTable
                    protocolList={protocols}
                    clearSearchValue={resetSearchValue}
                    handleDelete={refreshList}
                  />
                </div>
              </InfiniteScroll>
            )}
          </>
        )}
      </ProtocolContext.Provider>
    </GlassWorkspaceShell>
  );
};

export default ProtocolsPage;
